import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MapMarker } from '../../../../assets/images/icons/icon_location.svg';
import { useCartPricing } from '../../../../contexts/CartPricingContext';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import { centsToDollars } from '../../../../lib/util';
import styles from '../../../../styles/lineItem.module.scss';
import * as types from '../../../../types';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import useCartItemV2Enabled from '../../../../hooks/useCartItemV2Enabled';
import useCartHasOnlyFreeTrialBundles from '../../../../hooks/useCartHasOnlyFreeTrialBundles';

interface ShippingLineItemProps {
  customerName?: types.CustomerFullName;
  shippingAddress: types.Address;
  shippingOption: types.IShippingOption;
}

export default function ShippingLineItem({ customerName, shippingAddress, shippingOption }: ShippingLineItemProps) {
  const { t } = useTranslation();
  const { shippingInCents } = useCartPricing();
  const checkoutPaths = useCheckoutPaths();
  const showCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const useStripeCheckout = getFiGrowthBook().getFeatureValue('stripe-elements-checkout', false);
  const editPath = useStripeCheckout ? checkoutPaths.StripeCheckout : checkoutPaths.Shipping;
  const isCanadaFreeTrial = useCartItemV2Enabled();
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();
  const hideFreeTrialShipping = isCanadaFreeTrial && onlyFreeTrials;

  return (
    <div className={styles.lineItem}>
      <div className={styles.itemRow}>
        <div className={classNames(styles.lineItemImage, { [styles.greyBackground]: showCartTest })}>
          <MapMarker />
        </div>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>
            <div className={styles.lineItemPriceRow}>
              {/* sentry-mask class is used to mask text in Sentry replays */}
              <div className={classNames(styles.lineItemDetails, 'sentry-mask')}>
                {customerName && (
                  <div>
                    {customerName.firstName} {customerName.lastName}
                  </div>
                )}
                <div>{shippingAddress.line1}</div>
                {shippingAddress.line2 && <div>{shippingAddress.line2}</div>}
                <div>
                  {shippingAddress.city}
                  {', '}
                  {shippingAddress.state} {shippingAddress.zip}
                </div>
              </div>
              {!hideFreeTrialShipping && !!shippingInCents && (
                <div className={styles.lineItemPrice}>{t('price', { price: centsToDollars(shippingInCents) })}</div>
              )}
            </div>
            {!hideFreeTrialShipping && <div>{shippingOption.name}</div>}
          </div>
          <div className={styles.removeButtonRow}>
            <Link to={editPath}>{t('edit')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
